/* ==========================================================================
   BOUNTEOUS CONFIDENTIAL
   ___________________

   Copyright 2019 Bounteous
   All Rights Reserved.

   NOTICE:  All information contained herein is, and remains the property
   of Bounteous and its suppliers, if any. The intellectual and
   technical concepts contained herein are proprietary to Bounteous
   and its suppliers and are protected by trade secret or copyright law.
   Dissemination of this information or reproduction of this material
   is strictly forbidden unless prior written permission is obtained
   from Bounteous.
   ========================================================================== */

/**
 * Site Search Component
 * - site search works via URL Hash
 * - submit event updates window.location.hash, as does pagination
 * - on hashchange event, search functionality is fired via executeSearch() func
 */
import { Component, registerComponent } from 'js/component.js';
import {
    buildQueryString,
    parseHashParameters,
    safeHtml,
} from 'js/helpers.js';

const componentName = 'cmp-site-search';

class SiteSearch extends Component {

    /**
     * Build New Hash Param
     * @param {string} term - populate q={term}
     * @param {string} page - populate page={page}
     * @returns {string}
     */
    buildHashParam(term, page) {
        return `#${buildQueryString({ q: term, page })}`;
    }

    /**
     * Update Result label
     * @param {string} label - label to update with
     */
    updateResultLabel(label) {
        this.elements.resultLabel.innerHTML = safeHtml(label);
        this.elements.resultLabel.removeAttribute('style');
    }

    /**
     * Update Location Hash From Input
     */
    updateHashFromInput() {
        const newTerm = this.elements.input.value.trim();
        window.location.hash = this.buildHashParam(newTerm, 1);
    }

    /**
     * Update Search Term From Current Location Hash
     */
    updateStateFromHash() {
        this.term = parseHashParameters().q || '';
        this.page = parseHashParameters().page || '1';
    }

    /**
     * Fetch Search Results
     */
    fetchResults() {
        fetch(`${this.searchUrl}?q=${this.term}&page=${this.page}`)
            .then((response) => {
                if(!response.ok) {
                    throw new Error('Response failed');
                }
                return response.json();
            }).then((response) => {
                this.results = null;
                this.pagination = null;
                if(!response.data) return;
                if(!response.data.success || !response.data.results) return;

                this.updateResultLabel(response.data.label);

                this.results = response.data.results;
                this.updateResults();

                this.pagination = response.data.pagination;
                this.updatePagination();
            })
            .catch(() => {
                this.updateResultLabel(this.resultLabelText);
            });
    }

    /**
     * Update Search Results in DOM
     */
    updateResults() {
        if(!this.results) return;

        const liItemString = (item) => {
            const itemLink = safeHtml(item.link);
            const itemTitle = safeHtml(item.title);
            const itemDescription = safeHtml(item.description);
            return(`
                <li>
                    <a class="cmp-site-search__result-item" href="${itemLink}">${itemTitle}</a>
                    <p class="cmp-site-search__result-item--description">${itemDescription}</p>
                    <p><a class="cmp-site-search__result-item--url" href="${itemLink}">${itemLink}</a></p>
                </li>
            `);
        };
        this.elements.resultList.innerHTML = this.results.map(liItemString).join('');
        this.elements.resultList.removeAttribute('style');
    }

    /**
     * Add Pagination Listeners (appended Elements)
     */
    addPaginationListeners() {
        const pages = this.elements.pages();
        if(!pages) return;

        pages.forEach((item) => {
            const page = item.getAttribute('data-page');
            item.addEventListener('click', () => {
                if (item.classList.contains('active')) return false;
                window.location.hash = this.buildHashParam(this.term, page);
                return null;
            });
        });
    }

    /**
     * Set Active Page
     * @param {string} page - page to set to active
     */
    setActivePage(page) {
        const pageLinks = this.elements.pages();
        [...pageLinks].map((ele) => {
            const linkPage = ele.getAttribute('data-page');
            ele.classList.remove('active');
            if(linkPage === page) ele.classList.add('active');
            return null;
        });
    }

    /**
     * Update Pagination in DOM
     */
    updatePagination() {
        if(!this.pagination) return;

        const pageArr = [];
        if (this.pagination.totalPages > 1) {
            for (let i = this.pagination.totalPages; i > 0; i -= 1) {
                pageArr.push(i);
            }

            const liItemString = (item) => `
                    <li>
                        <a class="cmp-site-search__page" href="javascript:;" data-page="${item}">${item}</a>
                    </li>
            `;

            this.elements.pagesContainer.innerHTML = pageArr.reverse().map(liItemString).join('');
            this.elements.paginationContainer.removeAttribute('style');
        } else {
            this.elements.paginationContainer.setAttribute('style', 'display:none');
        }

        this.addPaginationListeners();
        this.setActivePage(this.page);
    }

    /**
     * Execute Search
     */
    executeSearch() {
        if(this.term === '') {
            // 1. Term is empty - update content to reflect
            this.updateResultLabel(this.resultLabelText);
        } else {
            // 2. Term is not empty - fire search
            this.elements.input.value = this.term;
            this.fetchResults();
        }
    }

    /**
     * Add Event Listeners
     */
    addEventListeners() {
        const classScope = this;

        // Update Page Hash on Submit
        function onSubmit(e) {
            e.preventDefault();
            classScope.updateHashFromInput();
        }

        // Execute Search on Hash Change
        function onHashChange() {
            classScope.updateStateFromHash();
            classScope.executeSearch();
        }

        // Update Page on Next Button Click
        function onNextClick() {
            const nextPage = parseInt(classScope.page, 10) + 1;
            if (nextPage > classScope.pagination.totalPages) return false;

            window.location.hash = classScope.buildHashParam(classScope.term, nextPage);
            return null;
        }

        // Update Page on Prev Button Click
        function onPrevClick() {
            const prevPage = parseInt(classScope.page, 10) - 1;
            if (prevPage < 1) return false;

            window.location.hash = classScope.buildHashParam(classScope.term, prevPage);
            return null;
        }

        // Assign Listeners
        this.elements.form.addEventListener('submit', onSubmit);
        this.elements.nextButton.addEventListener('click', onNextClick);
        this.elements.prevButton.addEventListener('click', onPrevClick);
        window.addEventListener('hashchange', onHashChange);
    }

    /**
     * Initialize Component
     */
    init() {
        // Set Up Elements
        this.elements = {
            form: this.element.querySelector(`.${componentName}__form`),
            input: this.element.querySelector(`.${componentName} input`),
            button: this.element.querySelector(`.${componentName}__button`),
            resultList: this.element.querySelector(`.${componentName}__results-list`),
            resultLabel: this.element.querySelector(`.${componentName}__results-label`),
            paginationContainer: this.element.querySelector(`.${componentName}__pagination-container`),
            pagesContainer: this.element.querySelector(`.${componentName}__pagination-pages`),
            pages: () => this.element.querySelectorAll(`.${componentName} [data-page]`),
            nextButton: this.element.querySelector(`.${componentName}__pagination-container .next`),
            prevButton: this.element.querySelector(`.${componentName}__pagination-container .previous`),
        };

        // Set Up Data
        this.term = '';
        this.page = '1';
        this.results = null;//will be array []
        this.pagination = null;//will be object {}
        this.searchUrl = this.element.getAttribute('data-searchurl');
        this.resultLabelText = this.elements.resultLabel.textContent;

        // Fire Init Funcs
        this.updateStateFromHash();
        this.addEventListeners();
        if(this.term !== '') this.executeSearch();
    }
}

registerComponent(`.${componentName}`, SiteSearch);
